import {
    isActiveEvent,
    mapApiEvent,
    parseEventIdFromSlug,
} from '@/lib/eventAdapter';

const API_URL = (import.meta.env.VITE_API_URL ?? 'https://back.awakasha.au').replace(
    /\/+$/,
    '',
);

async function requestJson(path, options = {}) {
    const response = await fetch(`${API_URL}${path}`, options);
    if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
    }
    return response.json();
}

async function fetchApiEvents() {
    const body = await requestJson('/api/events');
    return (body.data ?? []).map(mapApiEvent).filter(isActiveEvent);
}

export async function fetchEvents() {
    try {
        return await fetchApiEvents();
    }
    catch {
        return [];
    }
}

export async function fetchUpcomingEvents() {
    try {
        const events = await fetchApiEvents();
        return events.filter((event) => event.isUpcoming === true);
    }
    catch {
        return [];
    }
}

export async function fetchEventBySlug(slug) {
    const eventId = parseEventIdFromSlug(slug);

    if (eventId) {
        const body = await requestJson(`/api/events/${eventId}`);
        if (body?.data) {
            const mapped = mapApiEvent(body.data);
            if (isActiveEvent(mapped)) return mapped;
        }
    }

    const events = await fetchApiEvents();
    const match = events.find((event) => event.slug === slug);
    if (match) return match;

    throw new Error('Event not found');
}

export async function submitContactForm(payload) {
    const body = await requestJson('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    return body;
}

export function mediaUrl(path) {
    if (!path) return path;
    if (path.startsWith('http://')) {
        return path.replace(/^http:\/\//i, 'https://');
    }
    if (path.startsWith('http')) return path;
    if (path.startsWith('/assets/')) return path;
    return `${API_URL}${path}`;
}

export async function fetchHomeBanner() {
    try {
        const body = await requestJson('/api/site/home-banner');
        return body?.data ?? null;
    }
    catch {
        return null;
    }
}

export function bannerMediaUrl(banner) {
    if (!banner?.imageURL) return null;
    const url = mediaUrl(banner.imageURL);
    if (!url) return null;
    if (!banner.updatedAt) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${banner.updatedAt}`;
}

export async function fetchGalleryImages() {
    try {
        const body = await requestJson('/api/site/gallery', {
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache' },
        });
        return body?.data ?? [];
    }
    catch {
        return [];
    }
}

export async function fetchPodcasts() {
    try {
        const body = await requestJson('/api/site/podcasts');
        return body?.data ?? [];
    }
    catch {
        return [];
    }
}

export async function fetchReviews() {
    try {
        const body = await requestJson('/api/site/reviews');
        return body?.data ?? [];
    }
    catch {
        return [];
    }
}

// Backward-compatible alias used by existing components.
export const fetchJson = fetchEventBySlug;
